import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { ACCESSORIES } from '../data/accessories';
import { STAGES, getStage } from '../data/stages';
import { Character } from '../objects/Character';
import { DanceSystem } from '../systems/DanceSystem';
import { StageResult } from '../types';
import { COLORS, CSS, FONT, OUTLINE } from '../theme';
import { drawOutlinedRect } from '../ui/OutlinedShape';
import { fillBackdropStar } from '../utils/decorations';
import { getCharacterScaleFactor, getLayoutFrame } from '../utils/layout';
import { setLetterboxColor } from '../utils/letterbox';

export class ResultScene extends Phaser.Scene {
  private result!: StageResult;
  private character: Character | null = null;
  private danceSystem: DanceSystem | null = null;

  constructor() {
    super('ResultScene');
  }

  init(data: { result: StageResult }) {
    this.result = data.result;
    this.character = null;
    this.danceSystem = null;
  }

  create() {
    const { STAGE_W } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    this.drawBackdrop();

    const stage = getStage(this.result.stageId);

    // Header
    this.add
      .text(STAGE_W / 2, frame.fromTop(160), 'STAGE CLEAR', {
        fontFamily: FONT.display,
        fontSize: '26px',
        color: CSS.inkSoft,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setLetterSpacing(4);

    this.add
      .text(STAGE_W / 2, frame.fromTop(226), stage?.name ?? this.result.stageId, {
        fontFamily: FONT.display,
        fontSize: '64px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5);

    // Stars row — 3 outlined-circle badges with rounded-tip stars. Lifted up
    // and shrunk slightly to make room for the character stage below. Sits
    // ~120px below the stage-name baseline to clear the descenders of the
    // 64px display font.
    const starsY = frame.fromTop(348);
    const starGap = 114;
    const starsCenterX = STAGE_W / 2;
    const starContainers: Phaser.GameObjects.Container[] = [];
    for (let i = 0; i < 3; i++) {
      const filled = i < this.result.stars;
      const isMid = i === 1;
      const sx = starsCenterX + (i - 1) * starGap;
      const sy = starsY + (isMid ? -12 : 0);
      const r = 48;
      const fill = filled ? COLORS.yolk : COLORS.white;

      const c = this.add.container(sx, sy);
      const shadow = this.add.graphics();
      shadow.fillStyle(COLORS.ink, 1);
      shadow.fillCircle(0, 10, r);
      c.add(shadow);
      const body = this.add.graphics();
      body.fillStyle(fill, 1);
      body.fillCircle(0, 0, r);
      body.lineStyle(OUTLINE, COLORS.ink, 1);
      body.strokeCircle(0, 0, r);
      c.add(body);

      // Star glyph (slightly rounded tips)
      const sg = this.add.graphics();
      this.drawRoundedStar(sg, 26, filled ? CSS.white : 'transparent');
      c.add(sg);

      c.setScale(0.3);
      c.setAlpha(0);
      starContainers.push(c);
    }
    starContainers.forEach((c, i) => {
      this.tweens.add({
        targets: c,
        scale: i === 1 ? 1.08 : 1,
        alpha: 1,
        duration: 320,
        ease: 'Back.Out',
        delay: 280 + i * 220,
      });
    });

    // ───── Character stage (main attraction) ─────
    // Show the dog wearing the accessory they just earned. Each tier gets a
    // different entry animation; PERFECT gets a celebratory burst.
    this.drawCharacterStage(stage?.accessoryId);

    // ───── Two score cards side-by-side: stage score / cumulative score ─────
    // Cards + RETRY/NEXT + STAGES are all anchored to the bottom of the safe
    // zone. Positions are shifted ~28px up vs the original design so that
    // STAGES lands exactly on the safe-zone bottom (frame.fromBottom(100))
    // and never clips off on notched devices. Inter-row gaps preserve the
    // original visual rhythm: card↔button 32px, button↔stages 18px.
    const cardY = frame.fromBottom(370);
    const cardW = 280;
    const cardH = 140;
    const cardGap = 24;
    const leftCardX = STAGE_W / 2 - cardW / 2 - cardGap / 2;
    const rightCardX = STAGE_W / 2 + cardW / 2 + cardGap / 2;

    this.drawScoreCard(leftCardX, cardY, cardW, cardH, 'STAGE SCORE', `${this.result.earnedScore}`);
    this.drawScoreCard(rightCardX, cardY, cardW, cardH, 'TOTAL SCORE', `${this.result.cumulativeScore}`);

    // ───── Buttons (fully rounded pills) ─────
    const btnY = frame.fromBottom(218);
    const stageList = STAGES;
    const currentIdx = stage ? stage.index - 1 : -1;
    const nextStage =
      currentIdx >= 0 && currentIdx + 1 < stageList.length ? stageList[currentIdx + 1] : null;

    if (nextStage) {
      // Side-by-side: centers at ±160 → 280px buttons leave a 40px gap with
      // ~60px margin on each side of the 720-wide canvas.
      this.makeButton(STAGE_W / 2 - 160, btnY, 'RETRY', '↻', COLORS.white, CSS.ink, 280, () => {
        this.scene.start('GameScene', { stageId: this.result.stageId });
      });
      this.makeButton(STAGE_W / 2 + 160, btnY, 'NEXT', '→', COLORS.coral, CSS.white, 280, () => {
        this.scene.start('GameScene', { stageId: nextStage.id });
      });
    } else {
      this.makeButton(STAGE_W / 2, btnY, 'RETRY', '↻', COLORS.white, CSS.ink, 340, () => {
        this.scene.start('GameScene', { stageId: this.result.stageId });
      });
    }
    // STAGES uses slate (deep gray) so its fill lifts off the ink border/shadow.
    // Sits exactly at the safe-zone bottom edge so the button is always fully
    // visible above the home indicator. ~118px below the RETRY/NEXT row.
    this.makeButton(STAGE_W / 2, frame.fromBottom(100), 'STAGES', '↩', COLORS.slate, CSS.white, 340, () => {
      this.scene.start('StageSelectScene');
    });

    this.game.events.on('safeAreaChanged', this.handleSafeAreaChanged, this);
    this.events.once('shutdown', () => {
      this.game.events.off('safeAreaChanged', this.handleSafeAreaChanged, this);
    });
  }

  private handleSafeAreaChanged = () => {
    if (!this.scene.isActive()) return;
    this.scene.restart({ result: this.result });
  };

  private drawScoreCard(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    value: string,
  ) {
    drawOutlinedRect(this, x, y, w, h, {
      fill: COLORS.white,
      radius: 36,
      shadow: 14,
    });
    this.add
      .text(x, y - h / 2 + 30, label, {
        fontFamily: FONT.display,
        fontSize: '18px',
        color: CSS.inkSoft,
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setLetterSpacing(3);
    this.add
      .text(x, y + 14, value, {
        fontFamily: FONT.display,
        fontSize: '52px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5);
  }

  /**
   * Place a dog wearing the accessory they just earned at center stage, and
   * animate it differently per tier — PERFECT gets a jump + star burst, GOOD
   * a punchy pop, JUST a gentle ease-in. The dance keeps it lively while the
   * player reads scores.
   */
  private drawCharacterStage(accessoryId: string | undefined) {
    const frame = getLayoutFrame(this);
    const character = new Character(this);
    this.character = character;
    // Override the gameplay world position — Character's head pivot sits near
    // the bottom of the source image, so most of the artwork extends upward
    // from container.y. lerp keeps the pivot inside the safe zone; the ratio
    // is tuned so the bottom of the blanket clears the score-card row.
    const stageX = GAME_CONFIG.STAGE_W / 2;
    const stageY = frame.lerp(0.6) + 10;
    character.container.setPosition(stageX, stageY);
    character.worldX = stageX;
    character.worldY = stageY;

    // Wear the accessory matching this stage. If the accessory data isn't
    // available for some reason, fall back to the plain head.
    const acc = accessoryId ? ACCESSORIES[accessoryId] : null;
    if (acc) character.setHead(acc.headKey);

    // Result-screen scale — boosted above the gameplay baseline so the
    // accessory the player just earned reads at a glance. Multiplied by the
    // safe-zone factor so it still fits the available height on notched
    // devices.
    const baseScale = 1.25 * getCharacterScaleFactor(frame);
    character.container.setScale(0);
    character.container.setAlpha(0);

    // Light dance — slightly hyped BPM, a tad more angle than gameplay base.
    this.danceSystem = new DanceSystem(character, {
      bpm: 110,
      maxAngle: 8,
      lateralPx: 0,
      depthPx: 0,
    });

    const hitType = this.result.hitType;
    const tier = hitType ?? 'just';
    const introDelay = 600; // wait for stars to mostly land first
    const intro = this.tweens.add({
      targets: character.container,
      scale: tier === 'perfect' ? baseScale * 1.08 : tier === 'good' ? baseScale * 1.04 : baseScale,
      alpha: 1,
      duration: tier === 'perfect' ? 520 : tier === 'good' ? 460 : 420,
      ease: tier === 'just' ? 'Cubic.Out' : 'Back.Out',
      delay: introDelay,
    });

    if (tier === 'perfect') {
      // Settle back down to baseScale after the slight overshoot.
      this.tweens.add({
        targets: character.container,
        scale: baseScale,
        duration: 240,
        ease: 'Cubic.Out',
        delay: introDelay + 520,
      });
      // Celebratory hop right after the intro lands.
      this.tweens.add({
        targets: character.container,
        y: stageY - 32,
        duration: 260,
        ease: 'Cubic.Out',
        yoyo: true,
        delay: introDelay + 520,
      });
      // Star burst — 8 ★ particles fan out from above the character's head.
      this.time.delayedCall(introDelay + 520, () => {
        this.spawnStarBurst(stageX, stageY - 360);
      });
    } else if (tier === 'good') {
      this.tweens.add({
        targets: character.container,
        scale: baseScale,
        duration: 220,
        ease: 'Cubic.Out',
        delay: introDelay + 460,
      });
    }

    void intro;
  }

  /** Eight small yolk stars exploding outward from (cx, cy) with random spin
   * and Quad.In fade. Used to celebrate a PERFECT clear. */
  private spawnStarBurst(cx: number, cy: number) {
    const COUNT = 8;
    for (let i = 0; i < COUNT; i++) {
      const angle = (Math.PI * 2 * i) / COUNT + (Math.random() - 0.5) * 0.3;
      const dist = 140 + Math.random() * 60;
      const star = this.add.graphics();
      this.drawRoundedStar(star, 14, CSS.yolk);
      star.setPosition(cx, cy);
      star.setDepth(20);
      const endX = cx + Math.cos(angle) * dist;
      const endY = cy + Math.sin(angle) * dist;
      this.tweens.add({
        targets: star,
        x: endX,
        y: endY,
        rotation: (Math.random() - 0.5) * 4,
        scale: 0.5 + Math.random() * 0.4,
        duration: 520,
        ease: 'Back.Out',
      });
      this.tweens.add({
        targets: star,
        alpha: 0,
        duration: 320,
        ease: 'Quad.In',
        delay: 280,
        onComplete: () => star.destroy(),
      });
    }
  }

  update(time: number) {
    if (this.danceSystem) this.danceSystem.update(time);
  }

  private drawBackdrop() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    setLetterboxColor(COLORS.cream);
    const g = this.add.graphics();
    g.fillStyle(COLORS.cream, 1);
    g.fillRect(0, 0, STAGE_W, STAGE_H);

    // Scattered larger star decorations replacing the old dot confetti. Hand-placed
    // positions to avoid overlap with key UI (header, score cards, buttons).
    const yolkStars = [
      { x: 90, y: 100, r: 22 },
      { x: 620, y: 80, r: 18 },
      { x: 50, y: 690, r: 16 },
      { x: 670, y: 600, r: 20 },
      { x: 90, y: 1160, r: 18 },
      { x: 650, y: 1200, r: 22 },
      { x: 110, y: 1420, r: 16 },
      { x: 660, y: 1460, r: 18 },
    ];
    yolkStars.forEach((s) => fillBackdropStar(g, s.x, s.y, s.r, COLORS.yolk, 0.35));
    const coralStars = [
      { x: 240, y: 70, r: 14 },
      { x: 470, y: 110, r: 16 },
      { x: 30, y: 860, r: 12 },
      { x: 690, y: 820, r: 14 },
      { x: 60, y: 1320, r: 14 },
      { x: 670, y: 1340, r: 12 },
    ];
    coralStars.forEach((s) => fillBackdropStar(g, s.x, s.y, s.r, COLORS.coral, 0.32));
  }

  private makeButton(
    x: number,
    y: number,
    label: string,
    icon: string,
    fill: number,
    labelColor: string,
    width: number,
    onClick: () => void,
  ) {
    const h = 100;
    // Fully rounded pill (radius = h/2)
    drawOutlinedRect(this, x, y, width, h, {
      fill,
      radius: h / 2,
      shadow: 10,
      outline: OUTLINE,
    });

    // Icon flush against the left side, text centered in the full button width.
    const iconPadX = 36;
    this.add
      .text(x - width / 2 + iconPadX, y - 2, icon, {
        fontFamily: FONT.display,
        fontSize: '34px',
        color: labelColor,
        fontStyle: '900',
      })
      .setOrigin(0, 0.5);
    this.add
      .text(x, y - 2, label, {
        fontFamily: FONT.display,
        fontSize: '30px',
        color: labelColor,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setLetterSpacing(2);

    const hit = this.add.zone(x, y, width, h).setInteractive({ useHandCursor: true });
    hit.on('pointerup', onClick);
  }

  /**
   * Draw a 5-pointed star with slightly rounded outer tips. Inner valleys stay
   * sharp. Each tip is replaced by a quadratic Bezier whose control point is
   * the original sharp vertex; the curve enters/exits at fraction `cap` back
   * along each leg.
   *
   * Phaser Graphics has no quadraticCurveTo — we sample the Bezier as line
   * segments instead.
   */
  private drawRoundedStar(g: Phaser.GameObjects.Graphics, r: number, fillCss: string) {
    const inner = r * 0.42;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
      const radius = i % 2 === 0 ? r : inner;
      pts.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
    }

    const cap = 0.25; // fraction of leg length used for the rounded tip cap
    const STEPS = 6; // Bezier subdivision per tip

    // Flatten the path into a vertex list: inner → a1 → [curve samples → a2] → next inner …
    const pathPts: { x: number; y: number }[] = [];
    for (let i = 0; i < 5; i++) {
      const innerCur = pts[(i * 2 + 1) % 10];
      const tip = pts[(i * 2 + 2) % 10];
      const innerNext = pts[(i * 2 + 3) % 10];
      const a1x = tip.x + (innerCur.x - tip.x) * cap;
      const a1y = tip.y + (innerCur.y - tip.y) * cap;
      const a2x = tip.x + (innerNext.x - tip.x) * cap;
      const a2y = tip.y + (innerNext.y - tip.y) * cap;
      pathPts.push(innerCur, { x: a1x, y: a1y });
      // Sample quadratic Bezier B(t) = (1-t)^2 P0 + 2(1-t)t P1 + t^2 P2 from a1 → tip → a2
      for (let s = 1; s <= STEPS; s++) {
        const t = s / STEPS;
        const u = 1 - t;
        const bx = u * u * a1x + 2 * u * t * tip.x + t * t * a2x;
        const by = u * u * a1y + 2 * u * t * tip.y + t * t * a2y;
        pathPts.push({ x: bx, y: by });
      }
    }

    const tracePath = () => {
      g.beginPath();
      g.moveTo(pathPts[0].x, pathPts[0].y);
      for (let i = 1; i < pathPts.length; i++) g.lineTo(pathPts[i].x, pathPts[i].y);
      g.closePath();
    };

    if (fillCss !== 'transparent') {
      const numFill = parseInt(fillCss.replace('#', ''), 16);
      g.fillStyle(numFill, 1);
      tracePath();
      g.fillPath();
    }
    g.lineStyle(3, COLORS.ink, 1);
    tracePath();
    g.strokePath();
  }
}
